import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Cell,
  CheckboxInput,
  Dropdown,
  FilterContext,
  List,
  Option,
  Right,
  SimpleBlock,
  Text,
  TextLink,
  useFilterContext,
  useListContextRequired,
  usePopupManager,
  useShowContextRequired
} from 'ui-builder';
import { useSearchParams } from 'react-router-dom';
import InnerBlock from '../../shared/web/layout/InnerBlock';
import {
  GridLayout,
  IconButton,
  IconDeleteButton,
  IconType,
  InformationMessage,
  OpenFormIconButton,
  Popup,
  PopupText,
  Separator
} from 'features/nekst-widgets';
import {
  TEAM_SETUP_AREAS_UPDATED,
  TeamPermission,
  TeamSetupArea,
  TeamSetupAreasFilter,
  TeamSetupAreaStatus,
  useTeamSetupAreasService
} from 'nekst-api';
import {
  CreateTeamSetupAreaButton,
  CreateTeamSetupAreaButtonPortal
} from './CreateTeamSetupAreaForm';
import { ProjectTypeField, TextBadge, TextBadgeStyle, useStatesHelper } from 'features/tasks-forms';
import ActivateMarketForm, { ActivateProcessForm } from './ActivateMarketForm';
import styles from './TeamSetupAreasList.module.scss';
import useSettingsNavigator from '../settingsNavigator';
import {
  TeamSetupAreasLimitContext,
  useTeamSetupAreasLimitContextRequired
} from './TeamSetupAreasLimitContext';
import MarketRequestsLinkWidget from './requests/MarketRequestsLinkWidget';
import { CreateCustomSetupAreaButton, UpdateCustomSetupAreaForm } from './customprocesses';
import useFeatureToggleService from 'feature-toggle';
import { AccessChecker } from 'authorization-scope';
import { useTeamSetupAreasContextRequired } from './TeamSetupAreasContext';

function DeleteTeamSetupAreaButton() {
  const showContext = useShowContextRequired<TeamSetupArea>();
  const teamSetupAreasService = useTeamSetupAreasService();

  const title = showContext.data.isCustom ? 'process' : 'market';

  const { teamSetupAreas } = useTeamSetupAreasContextRequired();

  const numberOfActiveSetupAreas = teamSetupAreas.filter(area => area.status === TeamSetupAreaStatus.ACTIVE).length;

  const isButtonDisabled = numberOfActiveSetupAreas === 1;

  if (showContext.data!.status !== TeamSetupAreaStatus.ARCHIVED) {
    return (
      <IconDeleteButton
        deleteFunc={async () => {
          await teamSetupAreasService.archiveSetupArea(showContext.data!.id);
        }}
        message={`Are you sure you want to archive "${showContext.data!.name}"? All transactions in this ${title} will be archived.`}
        withConfirmationInput
        disabled={isButtonDisabled}
        title={isButtonDisabled
          ? `In order to remove this ${title}, please add a new Market or create a Custom Process to be used as the default. You can designate the Default Market or Process by clicking the star icon.`
          : undefined}
      />
    );
  } else {
    return null;
  }
}

function ArchivedBadge() {
  const { data } = useShowContextRequired<TeamSetupArea>();

  if (data.status === TeamSetupAreaStatus.ARCHIVED) {
    return (
      <TextBadge text="Archived" style={TextBadgeStyle.INACTIVE} />
    );
  } else {
    return null;
  }
}

function DefaultBadge() {
  const { data } = useShowContextRequired<TeamSetupArea>();

  if (data.isDefault) {
    return (
      <TextBadge text="Default" style={TextBadgeStyle.ACTIVE} />
    );
  } else {
    return null;
  }
}

function SetAsDefaultButton() {
  const { data } = useShowContextRequired<TeamSetupArea>();

  const teamSetupAreasService = useTeamSetupAreasService();

  if (data.status === TeamSetupAreaStatus.ACTIVE) {
    return (
      <IconButton
        onClick={async () => {
          if (data.isDefault) {
            return;
          }
          await teamSetupAreasService.setDefaultSetupArea(data.id);
        }}
        title={!data.isDefault ? 'Set as default' : undefined}
        type={data.isDefault ? IconType.STAR_SELECTED : IconType.STAR}
      />
    );
  } else {
    return null;
  }
}

function LimitReachedMessagePopup() {
  return (
    <Popup
      title="Limit Reached"
      content={
        <PopupText>
          You have reached the limit of markets for your plan. Please update your plan or contact
          support to increase your limit.
        </PopupText>
      }
    />
  );
}

function ProcessNameAndLinkField() {
  const showContext = useShowContextRequired<TeamSetupArea>();

  const settingsNavigator = useSettingsNavigator();

  const popupManager = usePopupManager();

  return (
    <TextLink
      onClick={() => {
        if (showContext.data.status === TeamSetupAreaStatus.ARCHIVED) {

          popupManager.openPopup(
            <ActivateProcessForm teamSetupArea={showContext.data} />
          );

        } else {
          settingsNavigator.openCustomProcessDetailFieldsPage(showContext.data.alias ? showContext.data.alias : showContext.data.id);
        }
      }}
    >
      {showContext.data.name}
    </TextLink>
  );
}

function MarketNameAndLinkField() {
  const showContext = useShowContextRequired<TeamSetupArea>();

  const settingsNavigator = useSettingsNavigator();

  const popupManager = usePopupManager();

  const { limits } = useTeamSetupAreasLimitContextRequired();

  return (
    <TextLink
      onClick={() => {
        if (showContext.data.status === TeamSetupAreaStatus.ARCHIVED) {

          if (limits.remaining > 0 || showContext.data.isCustom) {
            popupManager.openPopup(
              <ActivateMarketForm teamSetupArea={showContext.data} />
            );
          } else {
            popupManager.openPopup(
              <LimitReachedMessagePopup />
            );
          }
        } else {
          if (showContext.data.isCustom) {
            settingsNavigator.openCustomProcessDetailFieldsPage(showContext.data.alias ? showContext.data.alias : showContext.data.id);
          } else {
            settingsNavigator.openMarketDetailBoxesPage(showContext.data.alias ? showContext.data.alias : showContext.data.id);
          }

        }
      }}
    >
      {showContext.data.name}
    </TextLink>
  );
}

function StateField() {
  const showContext = useShowContextRequired<TeamSetupArea>();

  const statesHelper = useStatesHelper();

  const state = showContext.data.market?.state || '';

  if (state) {
    const country = statesHelper.getCountryByValue(state);

    let result = statesHelper.getLabelByValue(state);

    if (country !== 'United States') {
      result = `${result} (${country})`;
    }

    return (
      <Text>{result}</Text>
    );
  } else {
    return (
      <Text>-</Text>
    );
  }
}

function StateFilter() {
  const {
    data,
    version
  } = useListContextRequired<TeamSetupArea>();

  const statesHelper = useStatesHelper();

  const filterContext = useFilterContext<TeamSetupAreasFilter>();

  const [optionsMap, setOptionsMap] = useState<Record<string, Option>>({});

  useEffect(() => {
    const statesMap: Record<string, Option> = {};

    data
      .forEach((area) => {
        const state = area.market?.state || '';
        if (state && !statesMap[state]) {
          statesMap[state] = {
            label: statesHelper.getLabelByValue(state),
            country: statesHelper.getCountryByValue(state),
            value: state
          };
        }
      });

    setOptionsMap({
      ...optionsMap,
      ...statesMap
    });
  }, [version]);

  const options = useMemo(() => {

    if (optionsMap) {
      return Object.values(
        Object
          .values(optionsMap)
          .reduce((acc, stateOption) => {
            if (!acc[stateOption.country]) {
              acc[stateOption.country] = {
                label: stateOption.country,
                options: []
              };
            }

            acc[stateOption.country].options.push(stateOption);
            return acc;
          }, {} as Record<string, { label: string, options: Option[] }>))
        .map((countryOption) => {
          return {
            ...countryOption,
            options: countryOption.options.sort((a, b) => (a.label as string).localeCompare(b.label as string))
          };
        });

    } else {
      return undefined;
    }

  }, [Object.keys(optionsMap).length]);

  if (options) {
    return (
      <SimpleBlock className="form">
        <Dropdown
          options={options}
          source="state"
          placeholder="Select State"
          hideLabel
          className={styles.state}
          value={filterContext.filterValue?.state}
          onChangeCallback={(changeData) => {
            filterContext.setFilterValue({
              ...filterContext.filterValue,
              state: changeData.state
            });
          }}
        />
      </SimpleBlock>
    );
  } else {
    return null;
  }
}

function PageTitle() {
  const { limits } = useTeamSetupAreasLimitContextRequired();

  return (
    <Text>
      Markets ({limits.used}/{limits.total})
    </Text>
  );
}

function LimitReachedMessage() {
  const { limits } = useTeamSetupAreasLimitContextRequired();

  if (limits.remaining <= 0) {
    return (
      <InformationMessage>
        You have reached the limit of {limits.total} markets. Please update your plan or contact
        support to increase your limit.
      </InformationMessage>
    );
  } else {
    return null;
  }
}

function ShowDeletedMarketsCheckbox() {
  const filterContext = useFilterContext<TeamSetupAreasFilter>();

  const onlyActive = filterContext.filterValue?.onlyActive !== undefined
    ? filterContext.filterValue.onlyActive
    : true;

  return (
    <CheckboxInput
      source="onlyActive"
      onChangeCallback={() => {

        filterContext.setFilterValue({
          ...filterContext.filterValue,
          onlyActive: !filterContext.filterValue?.onlyActive
        });
      }}
      label="Show Archived"
      value={!onlyActive}
    />
  );
}

function UpdateCustomSetupAreaButton() {
  const showContext = useShowContextRequired<TeamSetupArea>();

  if (showContext.data.isCustom) {
    return (
      <OpenFormIconButton
        form={<UpdateCustomSetupAreaForm teamSetupArea={showContext.data} />}
        type={IconType.PENCIL}
      />
    );
  } else {
    return null;
  }
}

function TeamSetupAreasFilterContext(
  props: {
    children: ReactNode,
  }
) {
  return (
    <FilterContext<TeamSetupAreasFilter>
      initialValue={{
        onlyActive: true
      }}
    >
      {props.children}
    </FilterContext>
  );
}

function MarketsList(props: { openFormOnLoad?: boolean }) {
  const teamSetupAreasService = useTeamSetupAreasService();

  return (
    <TeamSetupAreasFilterContext>
      <TeamSetupAreasLimitContext>
        <InnerBlock
          title={<PageTitle />}
          headerRight={<CreateTeamSetupAreaButtonPortal />}
        >
          <List<TeamSetupArea, TeamSetupAreasFilter>
            getDataFunc={async (filter) => {
              const result = await teamSetupAreasService
                .getTeamSetupAreas(filter);

              return result.filter((item) => !item.isCustom);
            }}
            updateOnEvents={[TEAM_SETUP_AREAS_UPDATED]}
            loadDataOnFilterUpdate
          >
            <LimitReachedMessage />
            <CreateTeamSetupAreaButton openFormOnLoad={props.openFormOnLoad} />
            <Right>
              <ShowDeletedMarketsCheckbox />
            </Right>
            <SimpleBlock className={styles.filterRow}>
              <StateFilter />
            </SimpleBlock>
            <GridLayout
              noDataMessage={`No assigned markets. Click "Add Market" and assign a market to get started.`}
            >
              <Cell label="Name">
                <MarketNameAndLinkField />
                <ArchivedBadge />
                <DefaultBadge />
              </Cell>
              <Cell label="State">
                <StateField />
              </Cell>
              <Cell label="Actions" small>
                <SetAsDefaultButton />
                <DeleteTeamSetupAreaButton />
              </Cell>
            </GridLayout>
          </List>
          <MarketRequestsLinkWidget />
        </InnerBlock>
      </TeamSetupAreasLimitContext>
    </TeamSetupAreasFilterContext>
  );
}

function CustomProcessesList() {
  const teamSetupAreasService = useTeamSetupAreasService();

  return (
    <TeamSetupAreasFilterContext>
      <TeamSetupAreasLimitContext>
        <InnerBlock
          title="Custom Processes"
          headerRight={<CreateCustomSetupAreaButton />}
        >
          <List<TeamSetupArea, TeamSetupAreasFilter>
            getDataFunc={async (filter) => {
              const result = await teamSetupAreasService
                .getTeamSetupAreas(filter);

              return result.filter((item) => item.isCustom);
            }}
            updateOnEvents={[TEAM_SETUP_AREAS_UPDATED]}
            loadDataOnFilterUpdate
          >
            <Right>
              <ShowDeletedMarketsCheckbox />
            </Right>
            <GridLayout
              noDataMessage={`No processes found. Click "Create Custom Process" to create one.`}
            >
              <Cell label="Name">
                <ProcessNameAndLinkField />
                <ArchivedBadge />
                <DefaultBadge />
              </Cell>
              <Cell label="Type">
                <ProjectTypeField source="projectType" />
              </Cell>
              <Cell label="Actions" small>
                <UpdateCustomSetupAreaButton />
                <SetAsDefaultButton />
                <DeleteTeamSetupAreaButton />
              </Cell>
            </GridLayout>
          </List>
        </InnerBlock>
      </TeamSetupAreasLimitContext>
    </TeamSetupAreasFilterContext>
  );
}

export default function TeamSetupAreasList() {
  const featureToggleService = useFeatureToggleService();

  const [searchParams] = useSearchParams();
  const openFormOnLoad = searchParams.get('openForm');

  return (
    <AccessChecker permissionToCheck={TeamPermission.MANAGE_TEAM_SETUP_AREAS} show403Error>
      <TeamSetupAreasLimitContext>
        <MarketsList openFormOnLoad={!!openFormOnLoad} />
      </TeamSetupAreasLimitContext>
      {featureToggleService.isCustomMarketsEnabled() && (
        <>
          <Separator marginSize="big" />
          <CustomProcessesList />
        </>
      )}
    </AccessChecker>
  );
}
